import type { ReactNode } from "react";
import { Icon, type IconName } from "./Icon.tsx";

export function Alert({
  kind = "info",
  children,
}: {
  kind?: "info" | "danger";
  children: ReactNode;
}) {
  return (
    <div className={`alert alert-${kind}`} role={kind === "danger" ? "alert" : "status"}>
      <Icon name={kind === "danger" ? "alert" : "info"} />
      <div>{children}</div>
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint ? <div className="field-hint">{hint}</div> : null}
    </div>
  );
}

/** A labelled group of controls (tiles, a stepper) that no single `<label>` can point at. */
export function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="field">
      <legend className="field-label">{label}</legend>
      {children}
      {hint ? <div className="field-hint">{hint}</div> : null}
    </fieldset>
  );
}

export function Skeleton({
  height = 16,
  width = "100%",
}: {
  height?: number | string;
  width?: number | string;
}) {
  return <div className="skeleton" style={{ height, width }} aria-hidden="true" />;
}

export function Badge({
  kind,
  children,
}: {
  kind?: "accent" | "ok" | "danger";
  children: ReactNode;
}) {
  return <span className={`badge${kind ? ` badge-${kind}` : ""}`}>{children}</span>;
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

/** A segmented tab bar; the caller renders the panel for the selected value. */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: readonly { value: T; label: string; icon: IconName }[];
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          className="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
        >
          <Icon name={o.icon} />
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A bounded integer picked with − and + (touch-sized), announced as it changes. */
export function Stepper({
  id,
  value,
  min,
  max,
  onChange,
  fewerLabel,
  moreLabel,
}: {
  id: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  fewerLabel: string;
  moreLabel: string;
}) {
  return (
    <div className="stepper">
      <button
        type="button"
        className="btn btn-icon"
        aria-label={fewerLabel}
        aria-controls={id}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        <Icon name="minus" />
      </button>
      <output id={id} className="stepper-value num" aria-live="polite">
        {value}
      </output>
      <button
        type="button"
        className="btn btn-icon"
        aria-label={moreLabel}
        aria-controls={id}
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        <Icon name="plus" />
      </button>
    </div>
  );
}

/** Tiles for a small integer range; the pressed tile is the value. */
export function NumberTiles({
  value,
  from,
  to,
  onChange,
}: {
  value: number;
  from: number;
  to: number;
  onChange: (value: number) => void;
}) {
  const values: number[] = [];
  for (let n = from; n <= to; n += 1) values.push(n);
  return (
    <div className="segmented">
      {values.map((n) => (
        <button
          key={n}
          type="button"
          className="segment num"
          aria-pressed={value === n}
          onClick={() => onChange(n)}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

export function Empty({ icon, title, hint }: { icon: IconName; title: string; hint: string }) {
  return (
    <div className="empty">
      <span className="empty-glyph">
        <Icon name={icon} />
      </span>
      <strong>{title}</strong>
      <span className="muted">{hint}</span>
    </div>
  );
}

export { Icon };
